import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { FileType, RadioOrSelectOption, RootState } from '../../../../types/common.types';
import { Comment, HashTag, Member, Poll, Post, TribeTemplate } from '../../../../types/entity.types';

import { useApprovalApi } from '../../../../api/approval/useApprovalApi';
import { useCommentApi } from '../../../../api/comment/useCommentApi';
import { useDocumentApi } from '../../../../api/document/useDocumentApi';
import { usePushNotificationApi } from '../../../../api/notification/push/usePushNotificationApi';
import { usePollApi } from '../../../../api/poll/usePollApi';
import { usePostApi } from '../../../../api/post/usePostApi';
import { useReactionApi } from '../../../../api/reaction/useReactionApi';

import { setPost } from '../../../../slices/programme/tribe/tribe.slice';

import { APPROVED, PENDING, POST_SEARCH_OPTIONS, TRIBE_PAGE_SIZE } from '../../../../utils/constants';
import { containsImageOrVideo, generateUUID, isImage } from '../../../../utils/util';

import TribeInput from '../../../../components/screens/programme/tribe/tribe-input/TribeInput';

type Props = {
  placeholder: string;
  hashtags?: HashTag[];
  comment?: boolean;
  parent?: Post | Comment;
  onPostSuccess: () => void;
  focus?: boolean;
  searchType?: RadioOrSelectOption;
  replyingToComment?: boolean;
};

const TribeInputContainer = ({
  placeholder,
  hashtags = [],
  comment = false,
  parent,
  onPostSuccess,
  focus = false,
  searchType = POST_SEARCH_OPTIONS[0],
  replyingToComment = false
}: Props) => {
  const [files, setFiles] = useState<File[] | FileType[]>([]);

  const { tribeTemplate, post } = useSelector(({ tribe }: RootState) => tribe);
  const { members } = useSelector(({ member }: RootState) => member);
  const { user } = useSelector(({ system }: RootState) => system);

  const dispatch = useDispatch();

  const { programmeExternalReference } = useParams();

  const { sendPushNotification } = usePushNotificationApi();

  const { uploadDocuments: uploadDocumentsRequest } = useDocumentApi();

  const { createPost: createPostRequest, viewPosts, updatePost: updatePostRequest } = usePostApi();

  const { createComment: createCommentRequest } = useCommentApi();

  const { createPoll: createPollRequest, updatePoll: updatePollRequest } = usePollApi();

  const { viewReactions } = useReactionApi();

  const { viewApprovalCount } = useApprovalApi();

  useEffect(() => {
    if (post) {
      const { linkedMediaFiles } = post;
      setFiles((linkedMediaFiles as FileType[]) ?? []);
    }
  }, [dispatch, post]);

  const addAttachment = (attachment: File | undefined) => {
    if (!attachment) return;

    // @ts-expect-error id is being adding to File
    attachment.id = generateUUID();
    if (isImage(attachment.name)) {
      // @ts-expect-error src is being adding to File
      attachment.src = URL.createObjectURL(attachment);
      setFiles([...files, attachment]);
    } else {
      setFiles([...files, attachment]);
    }
  };

  const removeAttachment = (attachment: File | FileType) => {
    setFiles(
      files.filter((file) => {
        // @ts-expect-error id is added to new files
        if (attachment.id) {
          // @ts-expect-error new files have an id
          return file.id !== attachment.id;
        } else {
          return (file as FileType).documentExternalReference !== (attachment as FileType).documentExternalReference;
        }
      })
    );
  };

  const uploadDocuments = async () => {
    return new Promise<FileType[]>((resolve) => {
      const documents: FileType[] = [];
      const documentsToUpload: File[] = [];

      for (let file of files) {
        const fileAlreadyUploaded =
          ('documentExternalReference' in file && !!file.documentExternalReference) || ('link' in file && !!file.link);

        if (fileAlreadyUploaded) {
          if ('link' in file && !!file.link) {
            const beforeIdIndex = file.link.indexOf('/videos/');
            const id = file.link.substring(beforeIdIndex + 8, file.link.indexOf('?'));

            file = {
              ...file,
              link: `videos/${id}`
            };
          }

          documents.push(file);
        } else {
          documentsToUpload.push(file as File);
        }
      }

      if (documentsToUpload.length > 0) {
        uploadDocumentsRequest(
          {
            documents: documentsToUpload
          },
          (uploadedFiles) => {
            if (uploadedFiles && uploadedFiles.length > 0) {
              documents.splice(0, 0, ...uploadedFiles);
            }
            resolve(documents);
          }
        );
      } else {
        resolve(documents);
      }
    });
  };

  const createPost = async (text: string) => {
    const { approvePosts, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approvePosts) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles = await uploadDocuments();

    createPostRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        text,
        approvalStatus,
        linkedMediaFiles
      },
      () => {
        viewPosts({
          programmeExternalReference: programmeExternalReference!,
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType?.value
        });

        viewApprovalCount({ programmeExternalReference: programmeExternalReference! });
        onPostSuccess();
      }
    );
  };

  const updatePost = async (text: string) => {
    const linkedMediaFiles = await uploadDocuments();

    const { externalReference, pinned } = post as Post;

    updatePostRequest(
      {
        postExternalReference: externalReference!,
        text,
        linkedMediaFiles,
        pinned: !!pinned
      },
      () => {
        viewPosts({
          programmeExternalReference: programmeExternalReference!,
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType?.value
        });

        dispatch(setPost(undefined));
        viewApprovalCount({ programmeExternalReference: programmeExternalReference! });
        onPostSuccess();
      }
    );
  };

  const createComment = async (text: string, cleanUp: () => void) => {
    const { approveComments, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approveComments) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles = await uploadDocuments();

    createCommentRequest(
      {
        text,
        parentPostExternalReference: parent!.externalReference!,
        programmeExternalReference: programmeExternalReference!,
        approvalStatus,
        linkedMediaFiles
      },
      (commentExternalReference) => {
        if (commentExternalReference && user?.username !== parent?.userName && parent?.memberExternalReference) {
          sendPushNotification({
            recipientExternalReference: parent.memberExternalReference,
            title: `${user?.username} commented on your ${replyingToComment ? 'comment' : 'post'}`,
            body: text,
            data: JSON.stringify({
              screen: 'tribe-post-hierarchy',
              navigator: 'programme',
              params: { externalReference: commentExternalReference },
              programmeExternalReference
            })
          });
        }

        onPostSuccess();

        if (cleanUp) {
          cleanUp();
          setFiles([]);
        }
      }
    );
  };

  const createPoll = async (text: string, poll: Poll) => {
    const { approvePosts, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approvePosts) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles = await uploadDocuments();

    const { question, options, allowMultipleSelection, anonymous } = poll;

    createPollRequest(
      {
        text,
        approvalStatus,
        linkedMediaFiles,
        question,
        programmeExternalReference: programmeExternalReference!,
        options: options.map((option) => option.value),
        allowMultipleSelection: !!allowMultipleSelection,
        anonymous: !!anonymous
      },
      () => {
        viewPosts({
          programmeExternalReference: programmeExternalReference!,
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType?.value
        });

        viewApprovalCount({ programmeExternalReference: programmeExternalReference! });
        onPostSuccess();
      }
    );
  };

  const updatePoll = async (text: string, poll: Poll) => {
    const linkedMediaFiles = await uploadDocuments();

    const { externalReference, pinned } = post as Post;
    const { question, options, allowMultipleSelection, anonymous } = poll;

    updatePollRequest(
      {
        text,
        linkedMediaFiles,
        question,
        pollExternalReference: externalReference!,
        pinned: !!pinned,
        options: options.map((option) => option.value),
        allowMultipleSelection: !!allowMultipleSelection,
        anonymous: !!anonymous
      },
      () => {
        const { memberExternalReference } = members.find((member) => member.username === user?.username) as Member;

        viewPosts({
          programmeExternalReference: programmeExternalReference!,
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType?.value
        });

        viewReactions({ memberExternalReference: memberExternalReference! });
        dispatch(setPost(undefined));
        viewApprovalCount({ programmeExternalReference: programmeExternalReference! });
        onPostSuccess();
      }
    );
  };

  return (
    <TribeInput
      post={post}
      placeholder={placeholder}
      hashtags={hashtags}
      members={members}
      files={files}
      comment={comment}
      addAttachment={addAttachment}
      removeAttachment={removeAttachment}
      createPost={createPost}
      updatePost={updatePost}
      createComment={createComment}
      createPoll={createPoll}
      updatePoll={updatePoll}
      focus={focus}
    />
  );
};

export default TribeInputContainer;
